import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Loader2, X, FileText, CheckSquare, Square, Shield } from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';
import NavigationButtons from './NavigationButtons';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { setToken as setMemToken } from '../utils/authToken';
import { API_URL, API } from '../config';

const TERMS_AND_CONDITIONS_ES = `CLÁUSULA DE SANCIONES Y CONDUCTA DEL USUARIO

Al crear una cuenta en DamyxIA, el usuario acepta cumplir con las siguientes normas de conducta y reconoce que su incumplimiento puede resultar en las sanciones descritas a continuación:

1. NORMAS DE CONDUCTA

El usuario se compromete a:
• Utilizar la plataforma de manera responsable y ética
• No compartir contenido ofensivo, discriminatorio o ilegal
• No intentar vulnerar la seguridad de la plataforma
• No utilizar el servicio para actividades ilegales o fraudulentas
• Respetar los derechos de propiedad intelectual de terceros
• No acosar, intimidar ni difamar a otros usuarios
• No realizar spam ni publicidad no autorizada

2. SISTEMA DE SANCIONES PROGRESIVAS

2.1 Primera Infracción: ADVERTENCIA
• El usuario recibirá una notificación formal de advertencia
• Se registrará la infracción en el historial de la cuenta
• El usuario deberá reconocer la advertencia para continuar usando el servicio

2.2 Segunda Infracción: SUSPENSIÓN TEMPORAL
• Suspensión de la cuenta por un período de 7 días
• Durante la suspensión, el usuario no podrá acceder a ninguna funcionalidad
• Al finalizar la suspensión, se requerirá aceptar nuevamente los términos

2.3 Tercera Infracción: SUSPENSIÓN EXTENDIDA
• Suspensión de la cuenta por un período de 30 días
• Revisión obligatoria del caso por el equipo de moderación
• Posible restricción permanente de ciertas funcionalidades

2.4 Infracciones Graves o Reincidencia: ELIMINACIÓN PERMANENTE
• Eliminación definitiva de la cuenta sin posibilidad de recuperación
• Pérdida de todos los datos, conversaciones y configuraciones
• Prohibición de crear nuevas cuentas en la plataforma

3. INFRACCIONES GRAVES (SANCIÓN INMEDIATA)

Las siguientes conductas resultarán en eliminación inmediata de la cuenta:
• Actividades ilegales o promoción de las mismas
• Acoso grave o amenazas a otros usuarios
• Intentos de hackeo o vulneración de seguridad
• Distribución de malware o contenido malicioso
• Suplantación de identidad
• Explotación de menores en cualquier forma

4. DERECHO DE APELACIÓN

El usuario tiene derecho a:
• Solicitar revisión de cualquier sanción en un plazo de 15 días
• Presentar evidencia en su defensa
• Recibir respuesta en un plazo máximo de 7 días hábiles

5. ACEPTACIÓN

Al marcar la casilla de aceptación, el usuario confirma que:
• Ha leído y comprendido estas normas en su totalidad
• Acepta cumplir con todas las normas de conducta establecidas
• Reconoce y acepta el sistema de sanciones progresivas
• Entiende que el incumplimiento puede resultar en la pérdida de acceso

Última actualización: Diciembre 2025`;

const TERMS_AND_CONDITIONS_EN = `USER CONDUCT AND SANCTIONS CLAUSE

By creating an account on DamyxIA, the user agrees to comply with the following code of conduct and acknowledges that non-compliance may result in the sanctions described below:

1. CODE OF CONDUCT

The user commits to:
• Use the platform responsibly and ethically
• Not share offensive, discriminatory or illegal content
• Not attempt to breach platform security
• Not use the service for illegal or fraudulent activities
• Respect third-party intellectual property rights
• Not harass, intimidate or defame other users
• Not spam or engage in unauthorized advertising

2. PROGRESSIVE SANCTIONS SYSTEM

2.1 First Offense: WARNING
• User will receive a formal warning notification
• The offense will be recorded in account history
• User must acknowledge the warning to continue using the service

2.2 Second Offense: TEMPORARY SUSPENSION
• Account suspension for 7 days
• During suspension, user cannot access any functionality
• After suspension, user must re-accept terms

2.3 Third Offense: EXTENDED SUSPENSION
• Account suspension for 30 days
• Mandatory case review by moderation team
• Possible permanent restriction of certain features

2.4 Serious Offenses or Recidivism: PERMANENT DELETION
• Permanent account deletion without recovery option
• Loss of all data, conversations and settings
• Ban on creating new accounts on the platform

3. SERIOUS OFFENSES (IMMEDIATE SANCTION)

The following behaviors will result in immediate account deletion:
• Illegal activities or promotion thereof
• Serious harassment or threats to other users
• Hacking attempts or security breaches
• Distribution of malware or malicious content
• Identity theft
• Exploitation of minors in any form

4. RIGHT OF APPEAL

The user has the right to:
• Request review of any sanction within 15 days
• Present evidence in their defense
• Receive a response within a maximum of 7 business days

5. ACCEPTANCE

By checking the acceptance box, the user confirms that:
• They have read and understood these rules in full
• They agree to comply with all established conduct rules
• They acknowledge and accept the progressive sanctions system
• They understand that non-compliance may result in loss of access

Last updated: December 2025`;

const AuthPage = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [referralCode, setReferralCode] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // 🟥 CUADRO NEGRO 2026-02-26 — Flag de intención para bloquear submits
  // automáticos (autofill, "Go" del teclado mobile, password manager).
  // Solo se pone en true cuando el usuario click EXPLÍCITAMENTE el botón.
  const intentToSubmitRef = useRef(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // API ya importada desde config

  // Check if register mode from URL and get referral code
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setIsLogin(false);
    }
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      setIsLogin(false); // Switch to register if there's a referral code
    }
  }, [searchParams]);

  const handleSubmit = async (eventOrNull) => {
    // 🟥 CUADRO NEGRO 2026-02-26 — Solo procesa el login si la llamada vino
    // de un click EXPLÍCITO en el botón submit. Bloqueamos:
    //   • Enter en inputs (form submit nativo)
    //   • Autofill del password manager que dispara submit sintético
    //   • Botón "Go/Enviar" del teclado virtual mobile
    // El flag `intentToSubmit` se setea SOLO en el onClick del botón.
    if (eventOrNull && typeof eventOrNull.preventDefault === 'function') {
      eventOrNull.preventDefault();
    }
    if (!intentToSubmitRef.current) {
      // submit no intencional → no hacemos nada.
      return;
    }
    intentToSubmitRef.current = false; // reset para próximas interacciones
    setMessage({ type: '', text: '' });
    setIsLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = isLogin 
        ? { email, password }
        : { email, password, referral_code: referralCode || null, accepted_terms: acceptedTerms };
      
      const response = await axios.post(endpoint, payload);
      const data = response.data;

      if (isLogin) {
        setMemToken(data.access_token);
        // Limpiar datos del perfil anterior antes de cargar los nuevos
        localStorage.removeItem('userPhoto');
        localStorage.removeItem('userEmail');
        // Persist email and avatar from login response
        localStorage.setItem('userEmail', email);
        if (data.avatar) {
          try { localStorage.setItem('userPhoto', data.avatar); } catch {}
        }
        // 🟪 P25.7 · Guardar el estado premium INMEDIATAMENTE tras el
        // login para evitar la race con /auth/me. Sin esto, cuentas
        // recién promovidas a premium desde el Panel del Creador veían
        // "solo para premium" al tocar el toggle Claro/Oscuro.
        try {
          localStorage.setItem('isPremium', data.is_premium ? 'true' : 'false');
          localStorage.setItem('isAdmin', data.is_admin ? 'true' : 'false');
          if (data.plan_id) localStorage.setItem('planId', data.plan_id);
        } catch (_e) { /* ignore quota exceeded / disabled storage */ }
        setMessage({ type: 'success', text: t('common.success') });
        // Pass terms_update_required and current_terms_version to parent
        onLoginSuccess(
          data.access_token, 
          data.is_verified,
          data.terms_update_required || false,
          data.current_terms_version || 1
        );
        navigate('/');
      } else {
        const successMsg = language === 'es' 
          ? `¡Registro exitoso! Revisa tu email para verificar tu cuenta. Tu código de referido es: ${data.referral_code}`
          : `Registration successful! Check your email to verify your account. Your referral code is: ${data.referral_code}`;
        setMessage({ 
          type: 'success', 
          text: successMsg
        });
        setEmail('');
        setPassword('');
        setTimeout(() => setIsLogin(true), 4000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || t('common.error');
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 bg-zinc-950 welcome-bg" data-testid="auth-page-root">
      <NavigationButtons showBack={true} showHome={true} />
      
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector />
      </div>
      
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(99,102,241,0.08),transparent_50%)] pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Avatar size="lg" />
          </div>
          <h1 className="font-outfit text-4xl font-medium bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-zinc-500">
            DamyxIA
          </h1>
          <p className="text-zinc-400 mt-2">{t('welcome.subtitle')}</p>
        </div>

        <div className="sidebar-bg border border-zinc-800/50 border-custom rounded-2xl p-8 shadow-2xl">
          <div className="flex gap-2 mb-6 bg-zinc-900/50 bg-input p-1 rounded-lg">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                isLogin 
                  ? 'bg-indigo-600 text-white shadow-lg' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {t('auth.login')}
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                !isLogin 
                  ? 'bg-indigo-600 text-white shadow-lg' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {t('auth.register')}
            </button>
          </div>

          {message.text && message.type !== 'success' && (
            <div 
              data-testid="auth-message"
              className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20"
            >
              {message.text}
            </div>
          )}

          {/* Registration blocked - Phase de Revisión.
              Controlado por REACT_APP_REGISTRATION_OPEN. Default: bloqueado.
              En preview seteamos `true` para testing; producción permanece
              en `false` mientras no haya orden expresa del Owner. */}
          {!isLogin && process.env.REACT_APP_REGISTRATION_OPEN !== 'true' ? (
            <div className="text-center py-8 px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {language === 'es' ? 'Estamos en Fase de Revisión' : 'We are in Review Phase'}
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                {language === 'es' 
                  ? 'DamyxIA no está aceptando registros en este momento. Estamos trabajando para ofrecerte la mejor experiencia posible.' 
                  : 'DamyxIA is not accepting registrations at this time. We are working to offer you the best possible experience.'}
              </p>
              <p className="text-zinc-500 text-xs">
                {language === 'es' ? 'Vuelve pronto.' : 'Come back soon.'}
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 text-primary mb-2">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 text-muted" />
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-3 bg-zinc-900/50 bg-input border border-zinc-800/50 border-custom rounded-lg text-zinc-200 text-primary placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                  placeholder={language === 'es' ? 'tu@email.com' : 'your@email.com'}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 text-primary mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 text-muted" />
                <input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-11 pr-12 py-3 bg-zinc-900/50 bg-input border border-zinc-800/50 border-custom rounded-lg text-zinc-200 text-primary placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {isLogin && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              )}
            </div>

            {/* Terms and Conditions Checkbox - Only for Registration */}
            {!isLogin && (
              <div className="flex items-start gap-3 p-3 bg-zinc-900/30 rounded-lg border border-zinc-800/50">
                <button
                  type="button"
                  data-testid="terms-checkbox"
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  className="mt-0.5 text-indigo-400 hover:text-indigo-300 transition-colors flex-shrink-0"
                >
                  {acceptedTerms ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
                <label className="text-sm text-zinc-300 leading-relaxed">
                  {language === 'es' ? 'He leído y acepto las' : 'I have read and accept the'}{' '}
                  <button
                    type="button"
                    data-testid="view-terms-btn"
                    onClick={() => setShowTermsModal(true)}
                    className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors"
                  >
                    {language === 'es' ? 'Normas de Conducta y el Sistema de Sanciones' : 'Code of Conduct and Sanctions System'}
                  </button>
                </label>
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="button"
              onClick={(e) => {
                // 🟥 CUADRO NEGRO 2026-02-26 — Marcamos intención REAL antes
                // de invocar handleSubmit. Solo este path llega al backend.
                if (!e.isTrusted) return; // ignora clicks sintéticos
                intentToSubmitRef.current = true;
                handleSubmit(null);
              }}
              disabled={isLoading || (!isLogin && !acceptedTerms)}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isLogin ? t('auth.loggingIn') : t('auth.registering')}
                </>
              ) : (
                isLogin ? t('auth.login') : t('auth.register')
              )}
            </button>
          </form>
          )}

          {isLogin && (
          <div className="mt-6 text-center text-sm text-zinc-500 text-muted">
            <p>
              {t('auth.noAccount')}{' '}
              <button 
                onClick={() => setIsLogin(false)}
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                {t('auth.registerHere')}
              </button>
            </p>
          </div>
          )}
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div 
          data-testid="terms-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            className="relative w-full max-w-2xl max-h-[80vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-indigo-400" />
                <h2 className="text-lg font-semibold text-white">
                  {language === 'es' ? 'Normas de Conducta y Sistema de Sanciones' : 'Code of Conduct and Sanctions System'}
                </h2>
              </div>
              <button
                data-testid="close-terms-modal"
                onClick={() => setShowTermsModal(false)}
                className="p-1 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-zinc-300 text-sm leading-relaxed font-sans bg-transparent p-0 m-0">
                  {language === 'es' ? TERMS_AND_CONDITIONS_ES : TERMS_AND_CONDITIONS_EN}
                </pre>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-zinc-700 bg-zinc-800/50">
              <p className="text-xs text-zinc-500">
                {language === 'es' 
                  ? 'Al aceptar, confirmas que has leído y comprendido estas normas.' 
                  : 'By accepting, you confirm that you have read and understood these rules.'}
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="decline-terms-btn"
                  onClick={() => setShowTermsModal(false)}
                  className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
                <button
                  data-testid="accept-terms-btn"
                  onClick={() => {
                    setAcceptedTerms(true);
                    setShowTermsModal(false);
                  }}
                  className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  {language === 'es' ? 'Acepto las Normas' : 'I Accept the Rules'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
